import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import '../../../../styles/profile/components/import-profile-modal.css';

interface ImportProfileModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<void>;
  isLoading?: boolean;
}

export default function ImportProfileModal({ 
  open, 
  onClose, 
  onImport,
  isLoading = false
}: ImportProfileModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const allowedExtensions = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        setError('Please select a PDF, DOC, or DOCX file');
        setSelectedFile(null);
        return;
      }

      setError(null);
      setSelectedFile(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError('Please select a file to import');
      return;
    }

    try {
      await onImport(selectedFile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import profile. Please try again.');
    }
  };

  if (!open) return null;

  return (
    <div
      className="import-profile-modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="import-profile-modal">
        <div className="import-profile-modal-header">
          <h2 className="import-profile-modal-title">Import Profile from File</h2>
          <button
            type="button"
            className="import-profile-modal-close"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        <div className="import-profile-modal-body">
          <p className="import-profile-modal-description">
            Upload a PDF, DOC, or DOCX file to import your profile information.
          </p>

          {!selectedFile ? (
            <div className="import-profile-modal-upload-area" onClick={handleUploadClick}>
              <Upload size={32} className="import-profile-modal-upload-icon" />
              <p className="import-profile-modal-upload-text">
                Click to select a file or drag and drop
              </p>
              <p className="import-profile-modal-upload-hint">
                Supported formats: PDF, DOC, DOCX
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div className="import-profile-modal-file-preview">
              <FileText size={24} className="import-profile-modal-file-icon" />
              <div className="import-profile-modal-file-info">
                <p className="import-profile-modal-file-name">{selectedFile.name}</p>
                <p className="import-profile-modal-file-size">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                type="button"
                className="import-profile-modal-file-remove"
                onClick={handleRemoveFile}
              >
                <X size={16} />
              </button>
            </div>
          )}

          {error && (
            <div className="import-profile-modal-error">
              {error}
            </div>
          )}
        </div>

        <div className="import-profile-modal-footer">
          <button
            type="button"
            className="import-profile-modal-btn import-profile-modal-btn--cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="import-profile-modal-btn import-profile-modal-btn--import"
            onClick={handleImport}
            disabled={!selectedFile || isLoading}
          >
            {isLoading ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

